import React from 'react';
import styled from 'styled-components';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ServiceHero from '../../components/ServiceHero';
import SidebarForm from '../../components/SidebarForm';
import { Player } from '@lottiefiles/react-lottie-player';

const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 100vh;
	background: #ffffff;
`;

const ContentSection = styled.main`
	max-width: 1400px;
	width: 100%;
	margin: 0 auto;

	padding: 48px 20px 80px;

	box-sizing: border-box;

	/* 1536px+ */
	@media (min-width: 1536px) {
		max-width: 1500px;
		padding: 60px 24px 100px;
	}

	/* 1280px */
	@media (max-width: 1280px) {
		max-width: 1220px;
	}

	/* 1024px */
	@media (max-width: 1024px) {
		padding: 42px 18px 70px;
	}

	/* 768px */
	@media (max-width: 768px) {
		padding: 36px 16px 60px;
	}

	/* 640px */
	@media (max-width: 640px) {
		padding: 32px 14px 54px;
	}

	/* 480px */
	@media (max-width: 480px) {
		padding: 28px 12px 48px;
	}

	/* 320px */
	@media (max-width: 320px) {
		padding: 24px 10px 42px;
	}
`;

const Grid = styled.div`
	display: grid;

	grid-template-columns: 1fr 360px;

	gap: 32px;

	align-items: start;

	/* 1280px */
	@media (max-width: 1280px) {
		gap: 28px;
	}

	/* 1024px */
	@media (max-width: 1024px) {
		grid-template-columns: 1fr 320px;
	}

	/* 768px */
	@media (max-width: 768px) {
		grid-template-columns: 1fr;
		gap: 24px;
	}

	/* 480px */
	@media (max-width: 480px) {
		gap: 20px;
	}
`;

const Article = styled.article`
	background: transparent;

	h2 {
		font-family:
			"Cormorant Garamond",
			Georgia,
			serif;

		font-size: 2rem;

		font-weight: 600;

		margin-bottom: 14px;

		color: #111827;

		letter-spacing: -0.5px;
	}

	h3 {
		font-family:
			"Cormorant Garamond",
			Georgia,
			serif;

		font-size: 1.6rem;

		font-weight: 600;

		margin:
			2rem 0 1rem;

		color: #111827;
	}

	p {
		color: #475569;

		line-height: 1.9;

		margin-bottom: 16px;

		font-size: 1rem;

		font-family: "Inter", sans-serif;
	}

	ul,
	ol {
		margin:
			10px 0 24px 22px;

		color: #475569;

		li {
			margin-bottom: 12px;

			line-height: 1.8;

			font-family: "Inter", sans-serif;
		}
	}

	.cta-row {
		display: flex;

		gap: 12px;

		margin-top: 24px;

		a,
		button {
			background:
				linear-gradient(
					135deg,
					#42526B 0%,
					#64748B 100%
				);

			color: #ffffff;

			padding: 12px 22px;

			border-radius: 12px;

			font-weight: 600;

			text-decoration: none;

			border: none;

			cursor: pointer;

			font-family: "Inter", sans-serif;

			transition:
				transform 0.35s ease,
				box-shadow 0.35s ease;

			box-shadow:
				0 10px 24px rgba(66,82,107,0.20);

			&:hover {
				transform: translateY(-4px);

				box-shadow:
					0 16px 35px rgba(66,82,107,0.30);
			}
		}
	}

	/* 1536px+ */
	@media (min-width: 1536px) {
		h2 {
			font-size: 2.2rem;
		}

		p,
		li {
			font-size: 1.05rem;
		}
	}

	/* 1280px */
	@media (max-width: 1280px) {
		h2 {
			font-size: 1.9rem;
		}
	}

	/* 1024px */
	@media (max-width: 1024px) {
		h2 {
			font-size: 1.75rem;
		}

		h3 {
			font-size: 1.45rem;
		}
	}

	/* 768px */
	@media (max-width: 768px) {
		h2 {
			font-size: 1.6rem;
		}

		h3 {
			font-size: 1.35rem;
		}

		p,
		li {
			font-size: 0.95rem;
			line-height: 1.8;
		}
	}

	/* 640px */
	@media (max-width: 640px) {
		h2 {
			font-size: 1.45rem;
		}
	}

	/* 480px */
	@media (max-width: 480px) {
		h2 {
			font-size: 1.3rem;
		}

		h3 {
			font-size: 1.2rem;
		}

		p,
		li {
			font-size: 0.9rem;
			line-height: 1.7;
		}

		.cta-row {
			flex-direction: column;
		}

		.cta-row a {
			width: 100%;
			text-align: center;
		}
	}

	/* 320px */
	@media (max-width: 320px) {
		h2 {
			font-size: 1.15rem;
		}

		h3 {
			font-size: 1.05rem;
		}

		p,
		li {
			font-size: 0.82rem;
		}
	}
`;

const VisualGrid = styled.div`
	display: grid;

	grid-template-columns: 1fr 360px;

	gap: 24px;

	align-items: center;

	margin-bottom: 1rem;

	.visual-box {
		display: flex;

		align-items: center;
		justify-content: center;

		background:
			linear-gradient(
				180deg,
				#FCFAF7 0%,
				#F6F1E8 100%
			);

		border-radius: 20px;

		padding: 20px;

		box-shadow:
			0 10px 30px rgba(15,23,42,0.05);

		border:
			1px solid rgba(15,23,42,0.05);
	}

	.text-col {
		padding-right: 6px;
	}

	/* 1024px */
	@media (max-width: 1024px) {
		grid-template-columns: 1fr 320px;
	}

	/* 768px */
	@media (max-width: 768px) {
		grid-template-columns: 1fr;

		gap: 20px;
	}

	/* 480px */
	@media (max-width: 480px) {
		.visual-box {
			padding: 16px;
			border-radius: 18px;
		}
	}
`;

const TrustRow = styled.div`
	display: flex;

	gap: 14px;

	align-items: center;

	margin-top: 26px;

	padding-top: 20px;

	border-top:
		1px solid rgba(15,23,42,0.08);

	img {
		height: 42px;
		width: auto;
	}

	strong {
		font-family: "Inter", sans-serif;
		color: #111827;
	}

	/* 480px */
	@media (max-width: 480px) {
		img {
			height: 36px;
		}
	}
`;

const Side = styled.aside`
	position: relative;

	align-self: start;

	@media (min-width: 821px) {
		position: sticky;

		top: 120px;

		height: calc(100vh - 140px);
	}

	@media (max-width: 820px) {
		position: relative;
		height: auto;
	}

	.sidebar-inner {
		height: 100%;

		overflow: auto;

		padding-right: 6px;
	}
`;

const Arbitration = () => {
	return (
		<PageWrapper>

			<Header />

			<ServiceHero
				title="Arbitration & Dispute Resolution"
				subtitle="Strategic Arbitration representation, commercial dispute resolution, award enforcement, and mediation support tailored for businesses and individuals."
				breadcrumb="Arbitration"
			/>

			<ContentSection>

				<Grid>

					<Article>

						<VisualGrid>

							<div className="text-col">

								<h2>
									Strategic Arbitration &
									Dispute Resolution
								</h2>

								<p>
									We provide comprehensive
									Arbitration and dispute
									resolution services for
									businesses, corporations,
									and individuals. Our team
									assists clients in resolving
									complex commercial disputes
									with strategic precision and
									efficient representation.
								</p>

								<h3>
									Why choose us?
								</h3>

								<ul>

									<li>
										Experienced representation
										before arbitral tribunals
									</li>

									<li>
										Strategic dispute resolution
										and settlement advisory
									</li>

									<li>
										Expertise in contractual and
										commercial disputes
									</li>

									<li>
										Efficient handling of
										Arbitration proceedings and
										enforcement actions
									</li>

								</ul>

							</div>

							<div
								className="visual-box"
								aria-hidden
							>

								<Player
									src="/assets/Drafts/registration.json"
									autoplay
									loop
									style={{
										height: 260,
										width: '100%',
										maxWidth: 320
									}}
								/>

							</div>

						</VisualGrid>

						<h3>
							Our Arbitration services include:
						</h3>

						<ul>

							<li>
								Drafting & responding to
								Arbitration notices
							</li>

							<li>
								Representation before
								arbitrators & arbitral
								tribunals
							</li>

							<li>
								Section 9 petitions under the
								Arbitration and Conciliation Act
							</li>

							<li>
								Section 34 petitions under the
								Arbitration and Conciliation Act
							</li>

							<li>
								Enforcement & execution of
								arbitral awards
							</li>

							<li>
								Settlement negotiation &
								mediation support
							</li>

						</ul>

						<h3>
							Simple 4-step process
						</h3>

						<ol>

							<li>
								Initial consultation &
								dispute assessment
							</li>

							<li>
								Case strategy &
								document review
							</li>

							<li>
								Arbitration filing &
								tribunal representation
							</li>

							<li>
								Award enforcement or
								dispute resolution
							</li>

						</ol>

						<p>
							Our Arbitration practice focuses
							on delivering commercially
							effective and time-efficient
							dispute resolution solutions.
							We assist clients throughout
							the Arbitration process,
							from initial notices to final
							enforcement proceedings.
						</p>

						<div className="cta-row">

							<a href="/contact">
								Request a Consultation
							</a>

							<a href="tel:+918506874280">
								Call: +91 85068 74280
							</a>

						</div>

						<TrustRow>

							<img
								src="/assets/google-icon-svgrepo-com.svg"
								alt="Trusted by Google"
							/>

							<div>

								<strong>
									Trusted legal advisory
									services
								</strong>

								<div
									style={{
										color: '#64748b',
										fontSize: '0.95rem',
										fontFamily:
											'Inter, sans-serif'
									}}
								>
									Professional dispute
									resolution for businesses
									and individuals
								</div>

							</div>

						</TrustRow>

					</Article>

					<Side>

						<div className="sidebar-inner">
							<SidebarForm />
						</div>

					</Side>

				</Grid>

			</ContentSection>

			<Footer />

		</PageWrapper>
	);
};

export default Arbitration;